<template lang="pug">
  v-app
    IOPreloader(
      v-if="!hidePreloader || forcePreloader"
      :preloaderText="preloaderText"
    )
    IOHeader(
      :is-done="hidePreloader"
      :is-mobile="isMobile"
    )
    v-main(:class="{ 'is-done': !isLoadingPage }")
      keep-alive
        router-view(
          :is-mobile="isMobile"
          :show-sale-info-bar="showSaleInfoBar"
          @mint-success="showSaleInfoBar = false"
        )
    transition(name="slide-up")
      SaleInfoBar(
        v-if="showSaleInfoBar"
        ref="saleInfoBar"
        :contract-data="contractData"
        :is-mobile="isMobile"
      )
    transition(name="slide-left")
      NotificationBar(
        v-if="showNotificationBar"
        :type="notificationBarType"
        :text="notificationBarText"
        @close="showNotificationBar = false"
      )
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { ethers } from 'ethers';
import debounce from 'debounce';

import saleMixin from '@/mixins/sale-mixins';

import SCAddresses from '@/contracts/addresses.json';

export default {
  name: 'App',
  metaInfo: {
    title: 'Mint',
    titleTemplate: '%s - Imaginary Ones',
  },
  components: {
    IOPreloader: () => import('@/components/IOPreloader'),
    IOHeader: () => import('@/components/IOHeader'),
    SaleInfoBar: () => import('@/components/SaleInfoBar'),
    NotificationBar: () => import('@/components/NotificationBar'),
  },
  mixins: [saleMixin],
  data() {
    return {
      windowWidth: 1080,
      windowHeight: 1080,
      hidePreloader: false,
      forcePreloader: false,
      contractData: {},
      showSaleInfoBar: true,
      preloaderText: '',
      showNotificationBar: false,
      notificationBarType: 'error',
      notificationBarText: '',
    };
  },
  computed: {
    ...mapState(['isLoadingPage']),
    ...mapGetters('contracts', ['getIOAddress', 'getIOAbi', 'getIOContract', 'getIOContractData']),
    ...mapGetters('accounts', [
      'isUserConnected',
      'getProviderEthers',
      'getActiveAccountChecksum',
      'getIsMinting',
      'getAccountData',
      'getChainId',
    ]),
    isMobile() {
      // if vertical
      return this.windowWidth < 1024 && this.windowWidth < this.windowHeight;
    },
  },
  watch: {
    isLoadingPage: {
      handler(val) {
        if (!val) {
          this.hidePreloader = true;
        }
      },
      immediate: true,
    },
    async getProviderEthers(provider) {
      if (typeof SCAddresses.IO[this.getChainId] === 'undefined') {
        // Force to use mainnet on production
        if (process.env.VUE_APP_ENV === 'production') {
          this.setChainData('0x1');
        } else {
          this.notify({
            type: 'error',
            text: 'Incorrect network',
          });
          this.disconnectWeb3Modal();
          return;
        }
      }

      if (provider !== null && this.getIOContract === null) {
        this.preloaderText = 'Setting things up...';
        this.forcePreloader = true;
        this.showLoadingPage();

        // Set up contract data
        await this.loadIOContract();
        Vue.set(this.contractData, 'isPreSaleLive', await this.getIOContract.isPreSaleLive());
        Vue.set(this.contractData, 'isPublicSaleLive', await this.getIOContract.isPublicSaleLive());
        Vue.set(
          this.contractData,
          'floorPrice',
          parseFloat(ethers.utils.formatEther(await this.getIOContract.floorPrice()))
        );
        Vue.set(
          this.contractData,
          'remainingSupply',
          parseInt((await this.getIOContract.getRemainingSupply()).toString())
        );
        Vue.set(this.contractData, 'maxSupply', parseInt(process.env.VUE_APP_SC_MAX_SUPPLY));
        Vue.set(
          this.contractData,
          'publicSaleStartTime',
          parseInt((await this.getIOContract.publicSaleStartTime()).toString())
        );
        Vue.set(
          this.contractData,
          'publicSaleDropInterval',
          parseInt(process.env.VUE_APP_SC_PUBLIC_SALE_DROP_INTERVAL)
        );
        Vue.set(
          this.contractData,
          'publicSaleDropAmount',
          parseFloat(process.env.VUE_APP_SC_PUBLIC_SALE_DROP_AMOUNT)
        );
        Vue.set(this.contractData, 'hasPopulated', true);
        await this.setIOContractData(this.contractData);

        await this.setupAccountData();

        this.preloaderText = '';
        this.showSaleInfoBar =
          !(
            this.isPreSaleLive &&
            this.getAccountData.hasPopulated &&
            this.getAccountData.isWhitelisted &&
            this.getAccountData.allowedMintQuantity === this.getAccountData.mintedQuantity
          ) &&
          !(
            this.contractData.isPublicSaleLive &&
            this.getAccountData.hasPopulated &&
            this.getAccountData.hasMintedDA
          );
        this.hideLoadingPage();
        this.forcePreloader = false;
      }
    },
    async getActiveAccountChecksum() {
      if (this.getActiveAccountChecksum !== null && this.getIOContract !== null) {
        this.preloaderText = 'Switching Account...';
        this.forcePreloader = true;
        this.showLoadingPage();
        setTimeout(async () => {
          this.resetAccount();
          await this.setupAccountData();
          this.preloaderText = '';
          this.showSaleInfoBar =
            !(
              this.isPreSaleLive &&
              this.getAccountData.hasPopulated &&
              this.getAccountData.isWhitelisted &&
              this.getAccountData.allowedMintQuantity === this.getAccountData.mintedQuantity
            ) &&
            !(
              this.contractData.isPublicSaleLive &&
              this.getAccountData.hasPopulated &&
              this.getAccountData.hasMintedDA
            );
          this.hideLoadingPage();
          this.forcePreloader = false;
        }, 250);
      }
    },
    getIsMinting(isMinting) {
      if (isMinting) {
        this.forcePreloader = true;
        this.showLoadingPage();
      } else {
        this.hideLoadingPage();
        this.forcePreloader = false;
      }
    },
  },
  async beforeMount() {
    this.storeIOAbi();
    this.storeIOAddress();
  },
  mounted() {
    if (window) {
      this.setWindowHeightForCSS();
      this.updateWindowDimensions();
    }

    window.addEventListener(
      'resize',
      debounce(() => {
        this.setWindowHeightForCSS();
      }, 20)
    );

    window.addEventListener(
      'resize',
      debounce(() => {
        this.updateWindowDimensions();
      }, 300)
    );

    this.$root.$on('notify', this.notify);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    window.removeEventListener('resize', this.setWindowHeightForCSS);

    this.$root.$off('notify', this.notify);
  },
  methods: {
    ...mapActions('contracts', ['storeIOAbi', 'storeIOAddress', 'loadIOContract']),
    ...mapActions('accounts', ['connectWeb3Modal', 'disconnectWeb3Modal', 'resetAccount']),
    ...mapMutations(['hideLoadingPage', 'showLoadingPage']),
    ...mapMutations('contracts', ['setIOContractData']),
    ...mapMutations('accounts', ['setAccountData', 'setChainData']),
    setWindowHeightForCSS() {
      let vh = window.innerHeight * 0.01;

      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    },
    updateWindowDimensions() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    },
    async setupAccountData() {
      // Set up account data
      const mintedQuantity = parseInt(
        (await this.getIOContract.whitelistClaimed(this.getActiveAccountChecksum)).toString()
      );
      const hasMintedDA = parseInt(
        (await this.getIOContract.publicSaleClaimed(this.getActiveAccountChecksum)).toString()
      );
      const accountData = {
        hasPopulated: true,
        isWhitelisted: this.$getWlAddress(this.getActiveAccountChecksum) ? true : false,
        allowedMintQuantity: this.$getWlAddress(this.getActiveAccountChecksum) || 0,
        mintedQuantity,
        hasMintedDA,
      };
      this.setAccountData(accountData);
    },
    notify(data) {
      this.notificationBarType = data.type;
      this.notificationBarText = data.text;
      this.showNotificationBar = data.text !== '';
    },
  },
};
</script>

<style lang="scss">
@import url('https://use.typekit.net/tga2dde.css');
@import '~@/assets/icomoon/style.scss';
@import '~@/assets/styles/global.scss';
</style>
